import styled from '@emotion/styled'
import { ModalOverlay, ModalContent, Input, Textarea, Button, Box } from '@chakra-ui/core'

export const StyledModalOverlay = styled(ModalOverlay)`
  background: rgba(0, 0, 0, 0.4);
  animation: fadeIn 0.3s ease-out;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`

export const StyledModalContent = styled(ModalContent)`
  border-radius: 50px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
  animation: scaleIn 0.3s ease-out;
  direction: ltr;

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: scale(0.9);
    }
    to {
      opacity: 1;
      transform: scale(1);
    }
  }
`

export const ModalContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px 20px;
  text-align: center;
`

export const UserName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #2d3748;
  margin-bottom: 24px;
`

export const ModalTitle = styled.h2`
  font-size: 18px;
  font-weight: 700;
  color: #2d3748;
  margin-bottom: 8px;
`

export const ModalSubtitle = styled.p`
  font-size: 16px;
  color: #718096;
  margin-bottom: 24px;
`

export const StyledInput = styled(Input)<{ hasError?: boolean }>`
  width: 100%;
  padding: 12px 16px;
  border: 1px solid ${({ hasError }) => (hasError ? '#e53e3e' : '#e2e8f0')};
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: ${({ hasError }) => (hasError ? '8px' : '24px')};
  transition: all 0.2s ease;

  &:focus {
    border-color: ${({ hasError }) => (hasError ? '#e53e3e' : 'black')};
    box-shadow: 0 0 0 3px ${({ hasError }) => (hasError ? 'rgba(229, 62, 62, 0.1)' : 'rgba(102, 126, 234, 0.1)')};
  }
`

export const ErrorMessage = styled.span`
  color: #e53e3e;
  font-size: 13px;
  margin-bottom: 16px;
  text-align: left;
  width: 100%;
`

export const StyledTextarea = styled(Textarea)`
  width: 100%;
  height: 100px;
  padding: 12px 16px;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  font-size: 14px;
  margin-bottom: 24px;
  transition: all 0.2s ease;
  resize: none;

  &:focus {
    border-color: black;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
`

export const TextareaWithMic = styled(Box)`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  margin-bottom: 42px;
`

export const MicButton = styled(Button)<{ isListening: boolean; isDisabled?: boolean }>`
  width: 46px;
  height: 46px;
  border-radius: 50%;
  border: none;
  display: flex;
  z-index: 1000;
  align-items: center;
  justify-content: center;
  cursor: ${({ isDisabled }) => (isDisabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  background: ${({ isListening, isDisabled }) => (isDisabled ? '#e2e8f0' : isListening ? '#E11D48' : '#000')};
  color: ${({ isDisabled }) => (isDisabled ? '#a0aec0' : 'white')};
  animation: ${({ isListening }) => (isListening ? 'pulse 1.5s ease-in-out infinite' : 'none')};
  box-shadow: ${({ isListening }) =>
    isListening ? '0 0 20px rgba(225, 29, 72, 0.5), 0 3px 10px rgba(0, 0, 0, 0.25)' : '0 3px 10px rgba(0, 0, 0, 0.25)'};

  &:hover {
    transform: translateX(-50%) scale(1.05) !important;
    background: ${({ isListening, isDisabled }) => (isDisabled ? '#e2e8f0' : isListening ? '#E11D48' : '#000')};
  }
`

export const ActivateButton = styled(Button)`
  background: #000;
  color: white;
  font-size: 16px;
  font-weight: 600;
  padding: 12px 48px;
  border-radius: 8px;
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: #1a1a1a;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`

// AI Feed Step styles
export const AIFeedOptionsContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`

export const AIFeedOptionRow = styled.div<{ isSelected: boolean }>`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 14px;
  background: ${({ isSelected }) => (isSelected ? '#f7f3ff' : '#f7fafc')};
  border: 2px solid ${({ isSelected }) => (isSelected ? '#d8c2ff' : 'transparent')};
  border-radius: 12px;
  cursor: pointer;
  transition: all 0.2s ease;
  text-align: left;

  &:hover {
    background: ${({ isSelected }) => (isSelected ? '#f7f3ff' : '#edf2f7')};
  }
`

export const RadioCircle = styled.div<{ isSelected: boolean }>`
  width: 20px;
  height: 20px;
  min-width: 20px;
  border-radius: 50%;
  border: 2px solid ${({ isSelected }) => (isSelected ? '#b289fb' : '#cbd5e0')};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  margin-top: 2px;
`

export const RadioDot = styled.div`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  background: #b289fb;
`

export const AIFeedOptionContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`

export const AIFeedOptionTitle = styled.span`
  font-size: 14px;
  font-weight: 600;
  color: #2d3748;
`

export const AIFeedOptionDescription = styled.span`
  font-size: 13px;
  color: #718096;
  line-height: 1.4;
`

// Collapsed options stack when Custom is selected - cards stacked on top of each other
export const CollapsedOptionsStack = styled.div`
  position: relative;
  height: 60px;
  cursor: pointer;
  margin-bottom: 8px;
`

export const StackedOptionCard = styled.div<{ index: number; total: number }>`
  position: absolute;
  left: 0;
  right: 0;
  top: ${({ index }) => index * 6}px;
  z-index: ${({ total, index }) => total - index};
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 14px;
  background: #f7fafc;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  transform: scale(${({ index }) => 1 - index * 0.02});
  transition: all 0.2s ease;

  &:hover {
    background: #edf2f7;
  }
`

export const StackedOptionTitle = styled.span`
  font-size: 13px;
  font-weight: 500;
  color: #4a5568;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

// KPIs Step styles
export const KpiListContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-bottom: 24px;
`

export const KpiItemRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 12px;
  background: #f7fafc;
  border-radius: 8px;
  transition: all 0.2s ease;

  &:hover {
    background: #edf2f7;
  }
`

export const KpiLabel = styled.span`
  font-size: 14px;
  font-weight: 500;
  color: #2d3748;
`

export const AddKpiButton = styled.button`
  width: 100%;
  padding: 12px;
  background: white;
  border: 2px dashed #cbd5e0;
  border-radius: 8px;
  font-size: 14px;
  font-weight: 600;
  color: #718096;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: #000;
    color: #000;
    background: #f7fafc;
  }
`

export const AddKpiContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 12px;
  background: #f7fafc;
  border-radius: 8px;
`

export const ButtonGroup = styled.div`
  display: flex;
  gap: 8px;
`

export const SmallButton = styled.button<{ variant?: 'primary' | 'secondary'; disabled?: boolean }>`
  flex: 1;
  padding: 8px 16px;
  border-radius: 6px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${({ variant = 'primary', disabled }) => {
    if (disabled) {
      return `
        background: #e2e8f0;
        color: #a0aec0;
        cursor: not-allowed;
      `
    }
    if (variant === 'secondary') {
      return `
        background: white;
        color: #718096;
        border: 1px solid #cbd5e0;
        &:hover {
          background: #f7fafc;
        }
      `
    }
    return `
      background: #000;
      color: white;
      &:hover {
        background: #1a1a1a;
      }
    `
  }}
`
